'use client';

import { SESSION_MERGE_ONBOARDING_MODAL_ID } from '@/atoms/session-merge-onboarding-modal-ids';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { cn } from '@/lib/cn';
import { useSessionMergeOnboardingAnalytics } from '@/models/analytics/features/useSessionMergeOnboardingAnalytics';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { type ModalId } from '@heidi/schema/ml-scribe';
import { type ReactElement, useEffect } from 'react';
import { useEnableSessionMerge } from '../../hooks/useEnableSessionMerge';
import { EnableSessionMergeCard } from './enable-session-merge-card';

export type EnableSessionMergeCardPopoverProps = {
  /**
   * Queue id this popover is bound to. Defaults to the Home card; the settings
   * second-chance card passes `SESSION_MERGE_ONBOARDING_SETTINGS_MODAL_ID` so it
   * tracks a separate seen-state.
   */
  modalId?: ModalId;
};

/**
 * Card owns its surface chrome, so strip the PopoverContent defaults (incl. the
 * experimental ring/shadow). Mirrors `SessionlessEvidenceAwarenessPopover`.
 */
const floatingPopoverContentClassName = cn(
  'z-[60] w-[min(calc(100vw-2rem),15rem)] max-w-60',
  '!rounded-none !border-0 !bg-transparent !p-0 !shadow-none !ring-0',
  'data-[experimental=true]:!gap-0 data-[experimental=true]:!rounded-none',
  'data-[experimental=true]:!border-0 data-[experimental=true]:!shadow-none',
  'data-[experimental=true]:!ring-0'
);

/**
 * Floating, queue-gated awareness popover for the one-time "Enable Session
 * Merge" onboarding card. Visibility is owned by the modal queue
 * (`SESSION_MERGE_ONBOARDING_MODAL_ID`); `seen_modals` keeps it to once per user
 * across devices. Mirrors `SessionlessEvidenceAwarenessPopover` for the floating
 * shell and `PatientListIntroductionModal` for the queue wiring.
 *
 * "Try now" enables auto session merge (and shows the success toast) via
 * `useEnableSessionMerge`, then closes; the close (X / Escape) dismisses without
 * enabling. Both paths mark the modal seen through `handleClose`.
 */
export function EnableSessionMergeCardPopover({
  modalId = SESSION_MERGE_ONBOARDING_MODAL_ID,
}: EnableSessionMergeCardPopoverProps = {}): ReactElement {
  const { isOpen, handleClose } = useQueuedModal(modalId);
  const { enableSessionMerge, isEnabling } = useEnableSessionMerge();
  const { trackViewed, trackAccepted, trackDismissed } =
    useSessionMergeOnboardingAnalytics();

  // `session_merge_onboarding_viewed` — fire once the queue surfaces the promo.
  useEffect(() => {
    if (isOpen) trackViewed();
  }, [isOpen, trackViewed]);

  // Dismiss without enabling (X / escape). Kept separate from `handleTryNow` so
  // the accept path never also counts as a dismissal.
  const handleDismiss = () => {
    trackDismissed();
    handleClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) handleDismiss();
  };

  const handleTryNow = () => {
    trackAccepted();
    enableSessionMerge();
    handleClose();
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor
        className="pointer-events-none absolute bottom-3.5 start-2 z-[55] size-px"
        aria-hidden
      />
      <PopoverContent
        side="right"
        align="end"
        onInteractOutside={(event) => event.preventDefault()}
        className={floatingPopoverContentClassName}
      >
        <EnableSessionMergeCard
          onTryNow={handleTryNow}
          onDismiss={handleDismiss}
          isEnabling={isEnabling}
        />
      </PopoverContent>
    </Popover>
  );
}
